/**
 * StudioRibbon — the compact command bar for the Parcel Test-Fit Studio.
 *
 * Commands remain in one horizontal workstation row. Frequent operations are
 * direct buttons; related draw/place/view/data actions use accessible menus
 * that escape the scroll container with fixed positioning. Presentation only:
 * every operation is delegated to the Studio host.
 */
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Building2,
  Car,
  ChevronDown,
  Compass,
  Download,
  Factory,
  FileUp,
  Hexagon,
  Layers,
  Map as MapIcon,
  Mountain,
  MousePointer2,
  PanelLeft,
  PanelRight,
  PenLine,
  Redo2,
  Route,
  Ruler,
  Server,
  Undo2,
  Grid2x2,
  Spline,
  Volume2,
  VolumeX,
  Wand2,
  Wind,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import type { GenerationLayoutWindRose } from '../../../../types/generationImpact';
import { StudioPlacementIndicator } from './StudioPlacementIndicator';
import { StudioPopover, StudioPopoverDivider, StudioPopoverSection } from './StudioPopover';
import type {
  StudioLeftPane,
  StudioProjection,
  StudioRibbonTab,
  StudioTool,
} from './studioStore';

type MenuItemRole = 'menuitem' | 'menuitemradio' | 'menuitemcheckbox';

interface CommandButtonProps {
  active?: boolean;
  pressed?: boolean;
  disabled?: boolean;
  title?: string;
  testId?: string;
  activeClass?: string;
  menuRole?: MenuItemRole;
  iconOnly?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const CommandButton: React.FC<CommandButtonProps> = ({
  active = false,
  pressed,
  disabled = false,
  title,
  testId,
  activeClass = 'border-primary bg-primary/15 text-primary shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)]',
  menuRole,
  iconOnly = false,
  onClick,
  children,
}) => (
  <button
    type="button"
    role={menuRole}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`flex min-h-8 items-center justify-start gap-1.5 border text-[10px] font-semibold tracking-wide transition-colors duration-100 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 max-[599px]:min-h-11 ${
      iconOnly ? 'min-w-8 px-1.5 max-[599px]:min-w-11' : menuRole ? 'w-full px-2.5 py-1.5 text-left' : 'px-2 py-1'
    } ${active ? activeClass : 'border-border bg-card text-text-secondary hover:border-text-muted/60 hover:bg-background hover:text-text-primary'} rounded-md`}
    {...(testId ? { 'data-testid': testId } : {})}
    {...(menuRole === 'menuitemradio' || menuRole === 'menuitemcheckbox'
      ? { 'aria-checked': active }
      : {})}
    {...(menuRole == null && pressed != null ? { 'aria-pressed': pressed } : {})}
  >
    {children}
  </button>
);

const DRAW_TOOLS: ReadonlyArray<{
  id: StudioTool;
  label: string;
  icon: LucideIcon;
  testId: string;
  title: string;
  accent?: string;
}> = [
  {
    id: 'frontline',
    label: 'Frontline',
    icon: PenLine,
    testId: 'generation-frontline-draw',
    title: 'Draw the frontline alignment axis',
  },
  {
    id: 'draw-road',
    label: 'Road',
    icon: Route,
    testId: 'studio-place-draw-road',
    title: 'Sketch a road centerline',
  },
  {
    id: 'draw-custom-polygon',
    label: 'Custom footprint',
    icon: Hexagon,
    testId: 'studio-place-draw-custom-polygon',
    title: 'Sketch a free-form custom footprint',
  },
  {
    id: 'barrier-wall',
    label: 'Acoustic wall',
    icon: PenLine,
    testId: 'generation-barrier-draw-wall',
    title: 'Draw an acoustic wall screen',
    accent: 'border-cyan-500 bg-cyan-500/10 text-cyan-400',
  },
  {
    id: 'barrier-berm',
    label: 'Acoustic berm',
    icon: Mountain,
    testId: 'generation-barrier-draw-berm',
    title: 'Draw an acoustic berm screen',
    accent: 'border-cyan-500 bg-cyan-500/10 text-cyan-400',
  },
  {
    id: 'barrier-building',
    label: 'Acoustic building screen',
    icon: Building2,
    testId: 'generation-barrier-draw-building',
    title: 'Draw an acoustic building-screen outline',
    accent: 'border-cyan-500 bg-cyan-500/10 text-cyan-400',
  },
];

const PLACE_TOOLS: ReadonlyArray<{
  id: StudioTool;
  label: string;
  icon: LucideIcon;
  testId: string;
  title: string;
}> = [
  {
    id: 'place-parking',
    label: 'Parking',
    icon: Car,
    testId: 'studio-place-place-parking',
    title: 'Place a 60 × 30 m parking area',
  },
  {
    id: 'place-building',
    label: 'Building footprint',
    icon: Factory,
    testId: 'studio-place-place-building',
    title: 'Place a 40 × 25 m building',
  },
  {
    id: 'place-data-center',
    label: 'Data center',
    icon: Server,
    testId: 'studio-place-place-data-center',
    title: 'Place a 100 × 60 m data-center hall',
  },
  {
    id: 'place-generator',
    label: 'Generator',
    icon: Zap,
    testId: 'studio-place-place-generator',
    title: 'Place a 15 × 10 m generator pad',
  },
];

export interface StudioRibbonProps {
  /** One-release compatibility ribbon used while the V2 build flag is off. */
  legacy?: boolean;
  tool: StudioTool;
  onSetTool: (tool: StudioTool) => void;
  projection: StudioProjection;
  onSetProjection: (projection: StudioProjection) => void;
  /** @deprecated Ignored by the compact command bar. */
  ribbonTab: StudioRibbonTab;
  /** @deprecated Ignored by the compact command bar. */
  onSetRibbonTab: (tab: StudioRibbonTab) => void;
  leftPane?: StudioLeftPane;
  leftDockOpen?: boolean;
  onSetLeftPane?: (pane: StudioLeftPane) => void;
  onToggleLeftDock?: () => void;
  contentsOpen: boolean;
  onToggleContents: () => void;
  layoutPanelOpen: boolean;
  onToggleLayoutPanel: () => void;
  inspectorOpen: boolean;
  onToggleInspector: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  /** Assist › generate a clean auto-packed layout (clears manual pins). */
  onCreateStartingLayout: () => void;
  /** Whether any component is manually pinned (enables the reset action). */
  hasManualPlacements: boolean;
  /** Assist › grid-arrange the current multi-selection. */
  onPackSelection: () => void;
  /** Size of the current multi-selection (enables Pack Selection). */
  selectionCount: number;
  /** Placement readout: components fixed by a manual pin. */
  pinnedCount: number;
  /** Placement readout: components arranged automatically by the packer. */
  autoCount: number;
  /** Clear every pin — hand the whole layout back to the packer. */
  onUnpinAll?: () => void;
  /** Pin every packer-placed component where it currently sits. */
  onPinAll?: () => void;
  readOnly: boolean;
  noiseAvailable: boolean;
  noiseVisible: boolean;
  onToggleNoise: () => void;
  frontlineSet: boolean;
  onClearFrontline: () => void;
  windRose: GenerationLayoutWindRose | undefined;
  fetchingWind: boolean;
  onFetchWind: () => void;
  /** Wind-rose overlay visibility (a VIEW toggle, like noise). */
  windVisible?: boolean;
  onToggleWind?: () => void;
  onAlignCrosswind: () => void;
  onOpenImport: () => void;
  exporting: boolean;
  onExport: (format: 'kmz' | 'shapefile' | 'dxf' | 'dwg') => void;
  /** Layout-section popovers (Parcel · Orientation · Packing · Yield · Exclusions). */
  layoutControls?: React.ReactNode;
}

const LEGACY_TABS: ReadonlyArray<{ id: StudioRibbonTab; label: string }> = [
  { id: 'tools', label: 'Tools' },
  { id: 'place', label: 'Place' },
  { id: 'view', label: 'View' },
  { id: 'data', label: 'Data' },
];

const LegacyStudioRibbon: React.FC<StudioRibbonProps> = ({
  tool,
  onSetTool,
  projection,
  onSetProjection,
  ribbonTab,
  onSetRibbonTab,
  contentsOpen,
  onToggleContents,
  layoutControls,
  inspectorOpen,
  onToggleInspector,
  readOnly,
  noiseAvailable,
  noiseVisible,
  onToggleNoise,
  frontlineSet,
  onClearFrontline,
  fetchingWind,
  onFetchWind,
  windVisible = false,
  onToggleWind,
  onAlignCrosswind,
  onOpenImport,
  exporting,
  onExport,
}) => {
  const mapToolDisabled = readOnly || projection !== 'map';
  const toolButton = (command: (typeof DRAW_TOOLS)[number] | (typeof PLACE_TOOLS)[number]) => {
    const Icon = command.icon;
    return (
      <CommandButton
        key={command.id}
        active={tool === command.id}
        pressed={tool === command.id}
        disabled={mapToolDisabled}
        onClick={() => onSetTool(tool === command.id ? 'select' : command.id)}
        title={projection !== 'map' ? 'Switch to Map to use this tool' : command.title}
        testId={command.testId}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {command.label}
      </CommandButton>
    );
  };
  return (
    <div
      className="shrink-0 border-b border-border bg-card"
      data-testid="studio-ribbon"
      data-studio-shell="legacy"
      role="toolbar"
      aria-label="Test-fit studio legacy tools"
    >
      <div className="flex min-h-10 min-w-max items-center gap-1 overflow-x-auto border-b border-border/70 px-2">
        {layoutControls}
        <CommandButton
          pressed={contentsOpen}
          onClick={onToggleContents}
          testId="studio-toggle-contents"
          title="Contents"
        >
          <Layers className="h-3.5 w-3.5" aria-hidden /> Contents
        </CommandButton>
        <CommandButton
          pressed={inspectorOpen}
          onClick={onToggleInspector}
          testId="studio-toggle-inspector"
          title="Inspector"
        >
          <PanelRight className="h-3.5 w-3.5" aria-hidden /> Inspector
        </CommandButton>
        <div className="ml-2 flex self-stretch" role="tablist" aria-label="Studio ribbon sections">
          {LEGACY_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={ribbonTab === tab.id}
              onClick={() => onSetRibbonTab(tab.id)}
              className={`border-b-2 px-3 text-[9px] font-bold uppercase tracking-wider ${
                ribbonTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text-secondary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex min-h-12 min-w-max items-center gap-1 overflow-x-auto px-2">
        {ribbonTab === 'tools' && (
          <>
            <CommandButton
              active={tool === 'select'}
              pressed={tool === 'select'}
              onClick={() => onSetTool('select')}
              testId="studio-tool-select"
              title="Select"
            >
              <MousePointer2 className="h-3.5 w-3.5" aria-hidden /> Select
            </CommandButton>
            {DRAW_TOOLS.filter(command => (
              command.id === 'frontline' || command.id.startsWith('barrier-')
            )).map(toolButton)}
            {frontlineSet && (
              <CommandButton onClick={onClearFrontline} testId="generation-frontline-clear">
                <X className="h-3.5 w-3.5" aria-hidden /> Clear frontline
              </CommandButton>
            )}
            <CommandButton
              disabled={readOnly || fetchingWind}
              onClick={onFetchWind}
              testId="generation-wind-fetch"
            >
              <Wind className="h-3.5 w-3.5" aria-hidden /> {fetchingWind ? 'Loading…' : 'Wind'}
            </CommandButton>
            <CommandButton disabled={readOnly} onClick={onAlignCrosswind} testId="generation-crosswind-align">
              <Compass className="h-3.5 w-3.5" aria-hidden /> Crosswind
            </CommandButton>
          </>
        )}
        {ribbonTab === 'place' && (
          <>
            {DRAW_TOOLS.filter(command => (
              command.id === 'draw-road' || command.id === 'draw-custom-polygon'
            )).map(toolButton)}
            {PLACE_TOOLS.map(toolButton)}
          </>
        )}
        {ribbonTab === 'view' && (
          <>
            <CommandButton
              active={projection === 'map'}
              pressed={projection === 'map'}
              onClick={() => onSetProjection('map')}
              testId="studio-projection-map"
            >
              <MapIcon className="h-3.5 w-3.5" aria-hidden /> Map
            </CommandButton>
            <CommandButton
              active={projection === 'blueprint'}
              pressed={projection === 'blueprint'}
              onClick={() => onSetProjection('blueprint')}
              testId="studio-projection-blueprint"
            >
              <Ruler className="h-3.5 w-3.5" aria-hidden /> Blueprint
            </CommandButton>
            <CommandButton
              pressed={noiseVisible}
              disabled={!noiseAvailable}
              onClick={onToggleNoise}
              testId="generation-noise-toggle"
            >
              {noiseVisible
                ? <Volume2 className="h-3.5 w-3.5" aria-hidden />
                : <VolumeX className="h-3.5 w-3.5" aria-hidden />}
              Noise
            </CommandButton>
          </>
        )}
        {ribbonTab === 'data' && (
          <>
            <CommandButton disabled={readOnly} onClick={onOpenImport} testId="studio-import-open">
              <FileUp className="h-3.5 w-3.5" aria-hidden /> Import
            </CommandButton>
            <CommandButton
              disabled={exporting}
              onClick={() => onExport('kmz')}
              testId="studio-export-kmz"
            >
              <Download className="h-3.5 w-3.5" aria-hidden /> Export KMZ
            </CommandButton>
          </>
        )}
      </div>
    </div>
  );
};

export const StudioRibbon: React.FC<StudioRibbonProps> = (props) => {
  if (props.legacy) return <LegacyStudioRibbon {...props} />;
  const {
  tool,
  onSetTool,
  projection,
  onSetProjection,
  leftPane,
  leftDockOpen,
  onSetLeftPane,
  onToggleLeftDock,
  contentsOpen,
  onToggleContents,
  layoutPanelOpen,
  onToggleLayoutPanel,
  inspectorOpen,
  onToggleInspector,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onCreateStartingLayout,
  hasManualPlacements,
  onPackSelection,
  selectionCount,
  pinnedCount,
  autoCount,
  onUnpinAll,
  onPinAll,
  readOnly,
  noiseAvailable,
  noiseVisible,
  onToggleNoise,
  frontlineSet,
  onClearFrontline,
  windRose,
  fetchingWind,
  onFetchWind,
  windVisible = false,
  onToggleWind,
  onAlignCrosswind,
  onOpenImport,
  exporting,
  onExport,
  layoutControls,
  } = props;
  const activeDraw = DRAW_TOOLS.some(command => command.id === tool);
  const activePlace = PLACE_TOOLS.some(command => command.id === tool);
  const resolvedLeftPane = leftPane ?? (layoutPanelOpen ? 'layout' : 'layers');
  const resolvedLeftOpen = leftDockOpen ?? (layoutPanelOpen || contentsOpen);

  const chooseLeftPane = (pane: StudioLeftPane): void => {
    if (onSetLeftPane) {
      if (resolvedLeftOpen && resolvedLeftPane === pane && onToggleLeftDock) {
        onToggleLeftDock();
      } else {
        onSetLeftPane(pane);
      }
      return;
    }
    if (pane === 'layout') onToggleLayoutPanel();
    else onToggleContents();
  };

  const chooseTool = (nextTool: StudioTool, close: () => void): void => {
    close();
    onSetTool(tool === nextTool ? 'select' : nextTool);
  };

  return (
    <div
      className="relative shrink-0 overflow-x-auto overflow-y-hidden border-b border-border bg-card bg-gradient-to-b from-background/35 to-transparent shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]"
      data-testid="studio-ribbon"
      role="toolbar"
      aria-label="Test-fit studio commands"
      aria-orientation="horizontal"
    >
      <div className="flex min-h-11 min-w-max items-center gap-1.5 px-2">
        <div className="flex items-center gap-px border-r border-border/70 pr-2" role="group" aria-label="History">
          <CommandButton
            iconOnly
            disabled={!onUndo || !canUndo}
            onClick={() => onUndo?.()}
            title="Undo (Ctrl/Cmd+Z)"
            testId="studio-undo"
          >
            <Undo2 className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Undo</span>
          </CommandButton>
          <CommandButton
            iconOnly
            disabled={!onRedo || !canRedo}
            onClick={() => onRedo?.()}
            title="Redo (Ctrl/Cmd+Shift+Z)"
            testId="studio-redo"
          >
            <Redo2 className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Redo</span>
          </CommandButton>
        </div>

        <div className="flex items-center gap-1" role="group" aria-label="Edit commands">
          <CommandButton
            active={tool === 'select'}
            pressed={tool === 'select'}
            onClick={() => onSetTool('select')}
            title="Select and inspect components (V)"
            testId="studio-tool-select"
          >
            <MousePointer2 className="h-3.5 w-3.5" aria-hidden />
            Select
          </CommandButton>

          <StudioPopover
            label="Insert"
            surfaceLabel="Insert geometry and equipment"
            icon={PenLine}
            active={activeDraw || activePlace}
            disabled={readOnly}
            testId="studio-insert-menu"
          >
            {(close) => (
              <>
                {/* Draw = multi-point sketch; Place = single click. Both add
                    something to the plan, so they share one menu — and both now
                    work in either projection. */}
                <StudioPopoverSection label="Draw">
                  {DRAW_TOOLS.map(command => {
                    const Icon = command.icon;
                    return (
                      <CommandButton
                        key={command.id}
                        menuRole="menuitemradio"
                        active={tool === command.id}
                        disabled={readOnly}
                        onClick={() => chooseTool(command.id, close)}
                        title={command.title}
                        testId={command.testId}
                        {...(command.accent ? { activeClass: command.accent } : {})}
                      >
                        <Icon className="h-3.5 w-3.5" aria-hidden />
                        {command.label}
                      </CommandButton>
                    );
                  })}
                </StudioPopoverSection>
                <StudioPopoverDivider />
                <StudioPopoverSection label="Place">
                  {PLACE_TOOLS.map(command => {
                    const Icon = command.icon;
                    return (
                      <CommandButton
                        key={command.id}
                        menuRole="menuitemradio"
                        active={tool === command.id}
                        disabled={readOnly}
                        onClick={() => chooseTool(command.id, close)}
                        title={command.title}
                        testId={command.testId}
                      >
                        <Icon className="h-3.5 w-3.5" aria-hidden />
                        {command.label}
                      </CommandButton>
                    );
                  })}
                </StudioPopoverSection>
                {frontlineSet && (
                  <>
                    <StudioPopoverDivider />
                    <CommandButton
                      menuRole="menuitem"
                      onClick={() => {
                        close();
                        onClearFrontline();
                      }}
                      title="Clear the frontline axis"
                      testId="generation-frontline-clear"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                      Clear frontline
                    </CommandButton>
                  </>
                )}
              </>
            )}
          </StudioPopover>


        </div>

        <div className="flex items-center border-r border-border/70 pr-2">

        </div>

        <StudioPopover
          label="Overlays"
          surfaceLabel="Analysis overlays"
          icon={Layers}
          active={noiseVisible || windVisible}
          testId="studio-view-menu"
        >
          {(close) => (
            <StudioPopoverSection label="Analysis overlays">
                <CommandButton
                  menuRole="menuitemcheckbox"
                  active={noiseVisible}
                  disabled={!noiseAvailable && !noiseVisible}
                  onClick={() => {
                    close();
                    onToggleNoise();
                  }}
                  title={noiseAvailable
                    ? 'Toggle the noise dissipation bands'
                    : 'Add acoustic sources in Impacts to compute the noise field'}
                  testId="generation-noise-toggle"
                  activeClass="border-amber-500 bg-amber-500/10 text-amber-400"
                >
                  {noiseVisible
                    ? <Volume2 className="h-3.5 w-3.5" aria-hidden />
                    : <VolumeX className="h-3.5 w-3.5" aria-hidden />}
                  Noise contours
                </CommandButton>
                {/* One wind control, not two: with no data it fetches (and
                    reveals the rose); with data it toggles the overlay. */}
                <CommandButton
                  menuRole={windRose ? 'menuitemcheckbox' : 'menuitem'}
                  active={windVisible}
                  disabled={readOnly || fetchingWind}
                  onClick={() => {
                    close();
                    if (windRose) onToggleWind?.();
                    else onFetchWind();
                  }}
                  title={windRose
                    ? 'Show or hide the prevailing-wind rose on the canvas'
                    : 'Fetch the prevailing wind rose for this site'}
                  testId="generation-wind-toggle"
                >
                  <Wind className="h-3.5 w-3.5" aria-hidden />
                  {fetchingWind ? 'Fetching wind…' : windRose ? 'Wind rose' : 'Fetch wind rose'}
                </CommandButton>
            </StudioPopoverSection>
          )}
        </StudioPopover>

        <StudioPopover
          label="Data"
          surfaceLabel="Import, export and wind data"
          icon={FileUp}
          testId="studio-data-menu"
        >
          {(close) => (
            <>
              <StudioPopoverSection label="Import">
                <CommandButton
                  menuRole="menuitem"
                  disabled={readOnly}
                  onClick={() => {
                    close();
                    onOpenImport();
                  }}
                  title="Import CAD, shapefile or KMZ geometry"
                  testId="studio-import-open"
                >
                  <FileUp className="h-3.5 w-3.5" aria-hidden />
                  Import geometry…
                </CommandButton>
              </StudioPopoverSection>
              <StudioPopoverDivider />
              <StudioPopoverSection label="Export">
                {([
                  ['kmz', 'KMZ', 'Google Earth overlay'],
                  ['shapefile', 'Shapefile', 'Zipped ESRI shapefile (WGS84)'],
                  ['dxf', 'DXF', 'CAD drawing in local metres'],
                  ['dwg', 'DWG', 'AutoCAD binary via the server converter'],
                ] as const).map(([format, label, title]) => (
                  <CommandButton
                    key={format}
                    menuRole="menuitem"
                    disabled={exporting}
                    onClick={() => {
                      close();
                      onExport(format);
                    }}
                    title={title}
                    testId={`studio-export-${format}`}
                  >
                    <Download className="h-3.5 w-3.5" aria-hidden />
                    Export {label}
                  </CommandButton>
                ))}
              </StudioPopoverSection>
              {!readOnly && (
                <>
                  <StudioPopoverDivider />

                </>
              )}
            </>
          )}
        </StudioPopover>

        {/* Placement (the packer's MODE) sits directly beside Auto-layout
            (its inputs + actions) so the one feature reads as one cluster. */}
          <StudioPlacementIndicator
            pinnedCount={pinnedCount}
            autoCount={autoCount}
            readOnly={readOnly}
            {...(onUnpinAll ? { onUnpinAll } : {})}
            {...(onPinAll ? { onPinAll } : {})}
          />

        {layoutControls}

        <div className="ml-auto flex items-center gap-px border-l border-border/70 pl-2" role="group" aria-label="Studio panes">
          <CommandButton
            active={resolvedLeftOpen && resolvedLeftPane === 'layers'}
            pressed={resolvedLeftOpen && resolvedLeftPane === 'layers'}
            onClick={() => chooseLeftPane('layers')}
            title="Show or hide the layer tree"
            testId="studio-toggle-contents"
          >
            <Layers className="h-3.5 w-3.5" aria-hidden />
            Layers
          </CommandButton>
          <CommandButton
            active={inspectorOpen}
            pressed={inspectorOpen}
            onClick={onToggleInspector}
            title="Show or hide Inspector and Verify"
            testId="studio-toggle-inspector"
          >
            <PanelRight className="h-3.5 w-3.5" aria-hidden />
            Details
          </CommandButton>
        </div>
      </div>
    </div>
  );
};
